import { ShareholderRole, SocietyOrgChartEdge, SocietyOrgChartNode, SocietySalesModel } from 'src/app/services/society.service';

/**
 * Example structure for `?view=orgchart&demo=1`, after the whiteboard: a holding with a
 * Komplementär-GmbH, SPVs (GmbH & Co. KG) held as Kommanditist, grouped by Verkaufsmodell,
 * some of them umsatzsteuerpflichtig. Shows the org chart without touching any database.
 * Invented names; the boxes don't open anything in this mode.
 */
export const DEMO_SALES_MODELS: SocietySalesModel[] = [
  { id: 1, name: 'Eigenbestand' },
  { id: 2, name: 'Teil-PVA' },
  { id: 3, name: 'Ampega' },
  { id: 4, name: 'Enovos / WES-Green' },
];

const society = (
  id: number,
  name: string,
  salesModelId: number | null = null,
  vatLiable = false,
  managingDirectors: string[] = ['Anna Berger']
): SocietyOrgChartNode => ({
  key: `society:${id}`,
  type: 'society',
  id,
  name,
  salesModelId,
  vatLiable,
  managingDirectors,
});
const company = (id: number, name: string): SocietyOrgChartNode => ({ key: `company:${id}`, type: 'company', id, name });
const person = (id: number, name: string): SocietyOrgChartNode => ({ key: `person:${id}`, type: 'person', id, name });
const holds = (from: string, to: string, share: number | null, role: ShareholderRole | null = null): SocietyOrgChartEdge => ({
  from,
  to,
  share,
  role,
});

const GP = 'general_partner';
const LP = 'limited_partner';

export const DEMO_ORG_CHART_NODES: SocietyOrgChartNode[] = [
  person(1, 'Anna Berger'),
  person(2, 'Jonas Keller'),
  company(1, 'Nordwind Beteiligungs AG'),
  { ...society(1, 'Sonnenfeld Holding GmbH', null, true, ['Anna Berger', 'Jonas Keller', 'Miriam Schulz']), accountant: 'Petra Lang', accountantDeputy: 'Tim Roth' },
  society(2, 'Sonnenfeld Verwaltungs GmbH', null, true, ['Jonas Keller']),
  { ...society(3, 'Solarpark Musterstadt GmbH & Co. KG', 1, true), accountant: 'Petra Lang' },
  society(4, 'Solarpark Altdorf GmbH & Co. KG', 1, true),
  society(5, 'Solarpark Lindenau GmbH & Co. KG', 2),
  society(6, 'Solarpark Birkenhain GmbH & Co. KG', 3),
  // Joint venture: one Geschäftsführer from each side, plus the partner company itself.
  society(7, 'Solarpark Seeblick GmbH & Co. KG', 3, false, ['Anna Berger', 'Lukas Hoffmann', 'Nordwind Beteiligungs AG']),
  society(8, 'Solarpark Wiesengrund GmbH & Co. KG', 4, true, ['Jonas Keller', 'Miriam Schulz']),
  society(9, 'Solarpark Talblick UG', null, false, []),
];

export const DEMO_ORG_CHART_EDGES: SocietyOrgChartEdge[] = [
  holds('person:1', 'society:1', 60),
  holds('person:2', 'society:1', 40),
  holds('society:1', 'society:2', 100),
  // Each SPV: the Verwaltungs-GmbH as Komplementär (no capital), the holding as Kommanditist.
  ...[3, 4, 5, 6, 8].flatMap(id => [holds('society:2', `society:${id}`, null, GP), holds('society:1', `society:${id}`, 100, LP)]),
  // Teil-PVA / joint venture: two Kommanditisten.
  holds('society:2', 'society:7', null, GP),
  holds('society:1', 'society:7', 50, LP),
  holds('company:1', 'society:7', 50, LP),
  // society:9 has no lines: shown as its own group in the top row.
];
